/**
 * ============================================
 * USE CERTIFICATES HOOK
 * ============================================
 * 
 * Fetches SSL certificate and domain status from backend.
 */

import { useState, useEffect, useCallback } from 'react';
import { pollingConfig } from '@/config';
import { getCertificates, type Certificate, type DomainPurchased } from '@/services/api';

interface UseCertificatesResult {
  certificates: Certificate[];
  domainsPurchased: DomainPurchased[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useCertificates(): UseCertificatesResult {
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [domainsPurchased, setDomainsPurchased] = useState<DomainPurchased[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCertificates = useCallback(async () => {
    try {
      const result = await getCertificates();
      setCertificates(result.certificates || []);
      setDomainsPurchased(result.domains_purchased || []);
      setError(result.error || null);
    } catch (err) {
      console.error('Failed to fetch certificates:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchCertificates();
  }, [fetchCertificates]);

  // Auto-refresh (less frequent for certs)
  useEffect(() => {
    if (!pollingConfig.autoRefresh) return;
    
    const interval = setInterval(fetchCertificates, pollingConfig.certificatesRefresh);
    return () => clearInterval(interval);
  }, [fetchCertificates]);

  return {
    certificates,
    domainsPurchased,
    isLoading,
    error,
    refetch: fetchCertificates,
  };
}
